import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { History, User, Package, Folder, Clock, Search, Loader, Download } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../utils/apiService';
import { generateExcelReport } from '../../utils/excelGenerator';
import { generatePdfReport } from '../../utils/pdfGenerator';

const EditHistoryPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [editLogs, setEditLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEditLogs = async () => {
      try {
        setLoading(true);
        const response = await apiService.getStats();
        
        if (response.success) {
          const logs = [];
          
          // Agregar productos editados
          if (response.stats.recentProducts) {
            response.stats.recentProducts.forEach(product => {
              logs.push({
                id: `product-${product.id}`,
                timestamp: new Date(product.updated_at).toLocaleString('es-ES'),
                itemType: 'Producto',
                itemName: product.name,
                itemId: product.id,
                categoryName: product.category?.name || 'Sin categoría',
                price: product.price,
                sortDate: new Date(product.updated_at)
              });
            });
          }
          
          // Agregar categorías (usar created_at como proxy ya que no hay updated_at en la query)
          if (response.stats.categoriesWithCount) {
            response.stats.categoriesWithCount.forEach(category => {
              logs.push({
                id: `category-${category.id}`,
                timestamp: 'N/A', // No tenemos timestamp de categoría
                itemType: 'Categoría',
                itemName: category.name,
                itemId: category.id,
                productCount: category.productCount,
                sortDate: new Date(0) // Poner al final
              });
            });
          }
          
          // Ordenar por fecha más reciente
          logs.sort((a, b) => b.sortDate - a.sortDate);
          
          setEditLogs(logs);
        }
      } catch (error) {
        console.error('Error cargando historial de ediciones:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEditLogs();
    
    // Escuchar cambios de tenant
    window.addEventListener('tenantChanged', fetchEditLogs);
    return () => window.removeEventListener('tenantChanged', fetchEditLogs);
  }, []);

  if (!isAdmin) {
    navigate('/admin');
    return null;
  }

  const filteredLogs = editLogs.filter(log =>
    log.itemType.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.itemName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exportEditHistoryPdf = () => {
    const headers = ['Tipo', 'Nombre', 'Última Actualización', 'Detalles'];
    const rows = filteredLogs.map(log => {
      let details = '';
      if (log.itemType === 'Producto') {
        details = `Categoría: ${log.categoryName} | Precio: $${log.price}`;
      } else if (log.itemType === 'Categoría') {
        details = `${log.productCount} productos`;
      }
      return [
        log.itemType,
        log.itemName,
        log.timestamp,
        details
      ];
    });
    generatePdfReport('Historial de Ediciones', headers, rows, 'Historial_de_Ediciones');
  };

  const exportEditHistoryExcel = () => {
    const headers = ['Tipo', 'Nombre', 'Última Actualización', 'Detalles'];
    const rows = filteredLogs.map(log => {
      let details = '';
      if (log.itemType === 'Producto') {
        details = `Categoría: ${log.categoryName} | Precio: $${log.price}`;
      } else if (log.itemType === 'Categoría') {
        details = `${log.productCount} productos`;
      }
      return [
        log.itemType,
        log.itemName,
        log.timestamp,
        details
      ];
    });
    generateExcelReport('Historial_de_Ediciones', headers, rows);
  };

  const getItemTypeIcon = (type) => {
    switch (type) {
      case 'Producto': return <Package className="w-4 h-4 text-blue-500" />;
      case 'Usuario': return <User className="w-4 h-4 text-green-500" />;
      case 'Categoría': return <Folder className="w-4 h-4 text-purple-500" />;
      default: return <History className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <Loader className="w-12 h-12 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Historial de Ediciones</h2>
          <p className="text-xl text-gray-600">Rastrea los productos y categorías más recientes del sistema.</p>
        </motion.div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 mb-8 shadow-lg border border-white/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por tipo o nombre..."
                className="w-full pl-12 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
              />
            </div>
            <div className="flex gap-2 ml-4">
              <motion.button
                onClick={exportEditHistoryPdf}
                className="p-2 bg-red-100 rounded-full text-red-600 hover:bg-red-200 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Descargar PDF"
              >
                <Download className="w-5 h-5" />
              </motion.button>
              <motion.button
                onClick={exportEditHistoryExcel}
                className="p-2 bg-green-100 rounded-full text-green-600 hover:bg-green-200 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Descargar Excel"
              >
                <Download className="w-5 h-5" />
              </motion.button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl shadow-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-sm leading-normal">
                  <th className="py-3 px-6 text-left">Tipo</th>
                  <th className="py-3 px-6 text-left">Nombre</th>
                  <th className="py-3 px-6 text-left">Última Actualización</th>
                  <th className="py-3 px-6 text-left">Detalles</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 text-sm font-light">
                {filteredLogs.length > 0 ? (
                  filteredLogs.map((log) => (
                    <motion.tr
                      key={log.id}
                      className="border-b border-gray-200 hover:bg-gray-50"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <td className="py-4 px-6 text-left">
                        <div className="flex items-center">
                          {getItemTypeIcon(log.itemType)}
                          <span className="ml-2 font-medium">{log.itemType}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-left font-medium">{log.itemName}</td>
                      <td className="py-4 px-6 text-left">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 text-gray-400 mr-2" />
                          {log.timestamp}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-left text-gray-600">
                        {log.itemType === 'Producto' && (
                          <span>Categoría: {log.categoryName} | Precio: ${log.price}</span>
                        )}
                        {log.itemType === 'Categoría' && (
                          <span>{log.productCount} productos</span>
                        )}
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-8 text-center text-gray-500">
                      No se encontraron registros
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredLogs.length === 0 && (
            <motion.div
              className="text-center py-12 text-gray-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              No se encontraron registros de edición que coincidan con tu búsqueda.
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default EditHistoryPage;